export default class InteractionSystem {
  init({ element, camera, getVibes, getTerritories, onHover, onSelect, onSelectCluster }) {
    this.element = element
    this.camera = camera
    this.getVibes = getVibes
    this.getTerritories = getTerritories
    this.onHover = onHover
    this.onSelect = onSelect
    this.onSelectCluster = onSelectCluster

    this.pointer = { x: 0, y: 0 }
    this.pointerDown = false
    this.dragDistance = 0
    this.activeHoverId = null

    this.element.addEventListener('pointermove', this.handlePointerMove)
    this.element.addEventListener('pointerdown', this.handlePointerDown)
    this.element.addEventListener('pointerup', this.handlePointerUp)
    this.element.addEventListener('pointerleave', this.handlePointerLeave)
    this.element.addEventListener('wheel', this.handleWheel, { passive: false })
  }

  handlePointerMove = (event) => {
    const point = this.getPoint(event)

    if (this.pointerDown) {
      const deltaX = point.x - this.pointer.x
      const deltaY = point.y - this.pointer.y

      this.dragDistance += Math.hypot(deltaX, deltaY)
      this.camera.panBy(deltaX, deltaY)
      this.element.classList.add('is-dragging')
    }

    this.pointer = point

    const hoveredVibe = this.findVibeAtPoint(point.x, point.y)
    const hoveredTerritory = hoveredVibe ? null : this.findTerritoryAtPoint(point.x, point.y)

    this.activeHoverId = hoveredVibe?.id ?? null
    this.onHover(hoveredVibe)
    this.element.style.cursor = this.pointerDown
      ? 'grabbing'
      : hoveredVibe || hoveredTerritory
        ? 'pointer'
        : 'grab'
  }

  handlePointerDown = (event) => {
    this.pointerDown = true
    this.dragDistance = 0
    this.pointer = this.getPoint(event)
    this.pointerDownVibeId = this.findVibeAtPoint(this.pointer.x, this.pointer.y)?.id ?? null
    this.pointerDownTerritoryId = this.pointerDownVibeId
      ? null
      : this.findTerritoryAtPoint(this.pointer.x, this.pointer.y)?.id ?? null
    this.element.setPointerCapture(event.pointerId)
  }

  handlePointerUp = (event) => {
    const point = this.getPoint(event)
    const hoveredVibe = this.findVibeAtPoint(point.x, point.y)
    const hoveredTerritory = hoveredVibe ? null : this.findTerritoryAtPoint(point.x, point.y)
    const clickedSameVibe = hoveredVibe?.id && hoveredVibe.id === this.pointerDownVibeId
    const clickedSameTerritory = hoveredTerritory?.id && hoveredTerritory.id === this.pointerDownTerritoryId

    if (this.dragDistance < 6) {
      if (clickedSameVibe) {
        this.onSelectCluster(null)
        this.onSelect(hoveredVibe, { append: event.shiftKey })
      } else if (clickedSameTerritory) {
        this.onSelect(null, { preserveClusterSelection: true })
        this.onSelectCluster(hoveredTerritory)
      } else {
        this.onSelect(null)
        this.onSelectCluster(null)
      }
    }

    this.pointerDown = false
    this.pointerDownVibeId = null
    this.pointerDownTerritoryId = null
    this.dragDistance = 0
    this.element.classList.remove('is-dragging')
    this.element.style.cursor = hoveredVibe || hoveredTerritory ? 'pointer' : 'grab'

    if (this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId)
    }
  }

  handlePointerLeave = () => {
    if (this.pointerDown) {
      return
    }

    this.activeHoverId = null
    this.onHover(null)
    this.element.classList.remove('is-dragging')
    this.element.style.cursor = 'grab'
  }

  handleWheel = (event) => {
    event.preventDefault()

    const zoomDelta = event.deltaY < 0 ? 1.1 : 0.92
    this.camera.zoomAt(event.clientX, event.clientY, zoomDelta)
  }

  findVibeAtPoint(x, y) {
    const vibes = this.getVibes()

    for (let index = vibes.length - 1; index >= 0; index -= 1) {
      const vibe = vibes[index]
      const screen = this.camera.worldToScreen(vibe.x, vibe.y)
      const radius = Math.max(10, vibe.radius * this.camera.zoom)

      if (Math.hypot(screen.x - x, screen.y - y) <= radius + 6) {
        return vibe
      }
    }

    return null
  }

  findTerritoryAtPoint(x, y) {
    const territories = this.getTerritories ? this.getTerritories() : []

    for (let index = territories.length - 1; index >= 0; index -= 1) {
      const territory = territories[index]
      const screen = this.camera.worldToScreen(territory.center.x, territory.center.y)
      const interactionRadius = Math.max(18, Math.min(territory.interactionRadius * this.camera.zoom, 38))

      if (Math.hypot(screen.x - x, screen.y - y) <= interactionRadius) {
        return territory
      }
    }

    return null
  }

  getPoint(event) {
    const bounds = this.element.getBoundingClientRect()

    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    }
  }
}
